import { useCallback, useContext } from "react";

import { useTenant } from "@/contexts/tenant-context";
import { TimezoneScopeContext } from "@/contexts/timezone-scope";
import { UserContext } from "@/contexts/user-context";
import { useUser } from "@/contexts/user-provider";

import { useLocations } from "@/hooks/use-locations";

import {
  browserTimezone,
  currentLocationTier,
  isValidTimezone,
  locationTimezone,
  locationsSpanTimezones,
  resolveTimezone,
  sameClock,
} from "@/lib/timezone";

/**
 * React hooks for effective timezone resolution. This module reads React
 * context, so nothing reachable from a server route may import it: the
 * non-hook getter `getTimezone()` lives in the server-safe `@/lib/timezone`,
 * over tiers the providers write into `@/stores/timezone-store`, and is
 * deliberately not re-exported here. The hooks resolve the same chain reactively:
 * override > current location > tenant default (single-timezone tenants) >
 * browser.
 *
 * `useTimezone` and `useViewerTimezone` read the user context directly rather
 * than through `useUser()`, which throws without a provider: shared components
 * such as the measurement cards render in the patient portal, which mounts no
 * UserProvider. There the user and location tiers are empty and the zone falls
 * back to the browser's.
 */

/**
 * The current location's tiers. Looked up among every location, archived ones
 * included, so a provider whose location was archived keeps its clock.
 */
function useCurrentLocationTier(): {
  timezone: string | null;
  selected: boolean;
} {
  const userContext = useContext(UserContext);
  const currentUser = userContext?.currentUser;
  const currentLocationId = userContext?.currentLocationId;
  const { locations } = useLocations({
    currentUser,
    seeAllLocations: true,
    includeArchived: true,
  });
  return currentLocationTier(currentLocationId, locations);
}

/**
 * The clock for a screen about one location: that location's timezone, the
 * tenant default when the location has none, or the viewer's when the
 * location is unknown.
 */
export function useLocationTimezone(
  locationId: string | null | undefined,
): string {
  const { currentUser } = useUser();
  // A record keeps its location's clock after the location is archived.
  const { locations } = useLocations({
    currentUser,
    seeAllLocations: true,
    includeArchived: true,
  });
  const viewer = useViewerTimezone();
  const { tenant } = useTenant();
  const location = locationId
    ? locations.find((candidate) => candidate.id === locationId)
    : undefined;
  return locationTimezone(location, tenant?.defaultTimezone, viewer);
}

/**
 * `useLocationTimezone` for callers that learn the location later, such as an
 * event picked in a callback: the location's timezone, else the viewer's.
 *
 * `undefined` while the locations are still loading and the location is not
 * among them yet, so text a patient reads is not composed on the viewer's
 * clock and then sent before the location's arrives. With
 * `{ viewerWhileLoading: true }` it answers the viewer's zone instead, for
 * display that can settle a moment later.
 */
export function useLocationTimezoneOf(options: {
  viewerWhileLoading: true;
}): (locationId: string | null | undefined) => string;
export function useLocationTimezoneOf(): (
  locationId: string | null | undefined,
) => string | undefined;
export function useLocationTimezoneOf(options?: {
  viewerWhileLoading?: boolean;
}): (locationId: string | null | undefined) => string | undefined {
  const viewerWhileLoading = options?.viewerWhileLoading ?? false;
  const { currentUser } = useUser();
  const { locations, isLocationsLoading } = useLocations({
    currentUser,
    seeAllLocations: true,
    includeArchived: true,
  });
  const viewer = useViewerTimezone();
  const { tenant } = useTenant();
  const tenantDefaultTimezone = tenant?.defaultTimezone;
  // `null` is signed out, whose query is disabled, not loading.
  const isLoading = isLocationsLoading && currentUser !== null;
  return useCallback(
    (locationId) => {
      const location = locationId
        ? locations.find((candidate) => candidate.id === locationId)
        : undefined;
      if (locationId && !location && isLoading) {
        return viewerWhileLoading ? viewer : undefined;
      }
      return locationTimezone(location, tenantDefaultTimezone, viewer);
    },
    [locations, isLoading, tenantDefaultTimezone, viewer, viewerWhileLoading],
  );
}

/** Whether the tenant's locations span more than one timezone. */
export function useTenantSpansTimezones(): boolean {
  const currentUser = useContext(UserContext)?.currentUser;
  const { locations } = useLocations({ currentUser, seeAllLocations: true });
  const { tenant } = useTenant();
  return locationsSpanTimezones(locations, tenant?.defaultTimezone);
}

/**
 * The zone times are read and written in here: the enclosing
 * `TimezoneScope`'s when there is one (a screen about one location), otherwise
 * the viewer's own — see `useViewerTimezone`.
 */
export function useTimezone(): string {
  const scoped = useContext(TimezoneScopeContext);
  const viewer = useViewerTimezone();
  return scoped ?? viewer;
}

/**
 * The viewer's own effective timezone, ignoring any `TimezoneScope`: override >
 * current location > tenant default (single-timezone tenants) > browser.
 */
export function useViewerTimezone(): string {
  const currentUser = useContext(UserContext)?.currentUser;
  const currentLocation = useCurrentLocationTier();
  const tenantSpansTimezones = useTenantSpansTimezones();
  const { tenant } = useTenant();
  // Resolved from context rather than the timezone store so a render sees the
  // current zone instead of one commit behind — but through the same
  // `resolveTimezone` the store-backed `getTimezone()` uses, so the two paths
  // can differ in timing (by at most a commit) and never in logic. Invalid
  // tiers are dropped there, so a malformed tenant/location zone can't reach
  // `Intl` consumers and throw.
  return (
    resolveTimezone({
      timezoneOverride: currentUser?.timezoneOverride,
      currentLocationTimezone: currentLocation.timezone,
      hasCurrentLocation: currentLocation.selected,
      tenantDefaultTimezone: tenant?.defaultTimezone,
      tenantSpansTimezones,
    }) ?? browserTimezone()
  );
}

/**
 * True once every tier the effective timezone depends on has settled.
 *
 * `useTimezone()` falls back to the tenant/browser zone while the locations
 * query is still in flight, so one-shot effects that normalize an instant to a
 * calendar day (e.g. the calendar's deep-link reveal) must wait for this before
 * running — otherwise an event near midnight is anchored to the wrong day and
 * never corrected.
 */
export function useTimezoneResolved(): boolean {
  const { currentUser, currentLocationId } = useUser();
  const { isLoading: isTenantLoading } = useTenant();
  const { locations, isLocationsLoading } = useLocations({ currentUser });
  // The current location's clock is looked up among every location, archived
  // ones included, which is a query of its own.
  const { isLocationsLoading: isAllLocationsLoading } = useLocations({
    currentUser,
    seeAllLocations: true,
    includeArchived: true,
  });
  // `undefined` is a user still loading; `null` is signed out, which has no
  // locations query to wait on — it is disabled, not loading.
  if (currentUser === undefined) return false;
  if (currentUser === null) return true;
  // The override tier wins outright.
  if (isValidTimezone(currentUser.timezoneOverride)) return true;
  // Otherwise the tenant default and the locations (which decide whether that
  // default applies, even with no selected location) must both have settled.
  if (isTenantLoading || isLocationsLoading || isAllLocationsLoading) {
    return false;
  }
  // UserProvider selects a user's only location in an effect after the
  // locations load; until it has, the zone is about to change.
  return !(locations.length === 1 && currentLocationId !== locations[0].id);
}

/**
 * React hook returning timezone info for the mismatch-indicator UI.
 *
 * `isOverridden` now means "the effective timezone keeps a different clock from
 * the browser's", regardless of whether the difference comes from an explicit
 * preference or the clinic location. That keeps the banner ("Times are shown in
 * X, which differs from your browser's timezone") meaningful for the
 * clinic-location tier too. Zones are compared by clock, not name: a browser on
 * `America/Detroit` viewing an `America/New_York` clinic sees the same times.
 */
export function useTimezoneInfo(): {
  timezone: string;
  isOverridden: boolean;
  browserTimezone: string;
} {
  const timezone = useTimezone();
  const browser = browserTimezone();
  return {
    timezone,
    isOverridden: !sameClock(timezone, browser),
    browserTimezone: browser,
  };
}
